import orderBy from 'lodash/orderBy'

import {
  LIST_ITEM_NOTE,
  LIST_ITEM_LOADER,
  LIST_ITEM_PLACEHOLDER,
  LIST_KEY_ROOT,
} from '../constants'

/*
  Every list items is represented by an item object defining its type,
  position, data, section.

  This object helps rendering the tree, handling dropping zone and
  improve rendering simplicity.
*/
const defaultItem = {
  type: '', // Type of item, can be a note, loader, label
  key: '', // Unique key string to identify the item
  sectionKey: LIST_KEY_ROOT, // Can be 'root' or a noteId
  position: -1, // Position used to order items
  height: 0, // Height to compute the item offset
  data: {}, // All the data needed for the item component to render properly
}

const ITEM_HEIGHT = 40
const PLACEHOLDER_HEIGHT = 24

export default function mapPropsToListItems({
  allChannelNotes,
  expandedNoteIds,
  activeNoteId,
  channelId,
  publicShareToken,
}) {
  const sortedTopLevelNotesWithChildren = buildSubTree({
    allChannelNotes,
    expandedNoteIds,
    activeNoteId,
    channelId,
    publicShareToken,
  })

  const rootLoaderItem = mapLoaderToListItem({
    noteId: null,
    channelId,
    sectionKey: LIST_KEY_ROOT,
    publicShareToken,
  })

  return [...sortedTopLevelNotesWithChildren, rootLoaderItem]
}

function buildSubTree(
  {
    allChannelNotes,
    expandedNoteIds,
    activeNoteId,
    channelId,
    publicShareToken,
  },
  {
    parentNoteId = null,
    offset = 0,
    path = [],
    sectionKey = LIST_KEY_ROOT,
  } = {}
) {
  const topLevelNotes = allChannelNotes.filter(note => {
    const noteParentNoteId = note.parentNote && note.parentNote.get('id')
    return noteParentNoteId === parentNoteId
  })

  const sortedTopLevelNotes = orderBy(topLevelNotes, 'listPosition', 'desc')

  return sortedTopLevelNotes.reduce((items, note) => {
    const noteIsExpanded = expandedNoteIds.includes(note.id)
    const noteAsItem = mapNoteToListItem(note, {
      channelId,
      offset,
      sectionKey,
      path,
    })

    const childrenSectionKey = note.id

    if (noteIsExpanded) {
      const noteLoaderItem = mapLoaderToListItem({
        noteId: note.id,
        channelId,
        sectionKey: childrenSectionKey,
        offset,
        publicShareToken,
      })
      const childrenItems = buildSubTree(
        {
          allChannelNotes,
          expandedNoteIds,
          activeNoteId,
          channelId,
          publicShareToken,
        },
        {
          parentNoteId: note.id,
          offset: offset + 1,
          sectionKey: childrenSectionKey,
          path: [...path, note.id],
        }
      )
      if (childrenItems.length < 1) {
        const placeholderItem = mapPlaceholderToListItem({
          offset: offset + 1,
          channelId,
          noteId: note.id,
          sectionKey: childrenSectionKey,
        })
        return [...items, noteAsItem, placeholderItem, noteLoaderItem]
      }

      return [...items, noteAsItem, ...childrenItems, noteLoaderItem]
    }
    return [...items, noteAsItem]
  }, [])
}

function mapNoteToListItem(
  note,
  { channelId, sectionKey = LIST_KEY_ROOT, offset = 0, path = [] } = {}
) {
  return {
    ...defaultItem,
    type: LIST_ITEM_NOTE,
    key: `${LIST_ITEM_NOTE}-${note.id}-${sectionKey}`,
    sectionKey,
    position: note.listPosition,
    height: ITEM_HEIGHT,
    data: {
      channelId,
      noteId: note.id,
      iconShape: note.iconShape,
      iconColor: note.iconColor,
      hasChildren: note.hasChildren,
      offset,
      path,
    },
  }
}

function mapLoaderToListItem({
  channelId,
  publicShareToken,
  sectionKey = LIST_KEY_ROOT,
  noteId = null,
  offset = 0,
} = {}) {
  return {
    ...defaultItem,
    type: LIST_ITEM_LOADER,
    key: `${LIST_ITEM_LOADER}-${sectionKey}-${channelId}-${noteId}`,
    sectionKey,
    data: {
      noteId,
      channelId,
      offset,
      publicShareToken,
    },
  }
}

function mapPlaceholderToListItem({
  channelId,
  noteId,
  sectionKey,
  offset,
} = {}) {
  return {
    ...defaultItem,
    sectionKey,
    type: LIST_ITEM_PLACEHOLDER,
    key: `${LIST_ITEM_PLACEHOLDER}-${noteId}`,
    height: PLACEHOLDER_HEIGHT,
    data: {
      noteId,
      channelId,
      offset,
    },
  }
}
